// Numbered evidence-pin placement for diagnosis photos (web counterpart of
// mobile/app/scan/diagnosis.tsx — center pins, not rectangles: vision-model
// boxes are noisy, and a misaligned rectangle reads as a bug while a center
// pin degrades gracefully). Pins are markers with name labels, not
// navigation — detail cards for problems 2+ live behind the plan gate.
//
// The photo renders with object-cover in a fixed-aspect container, which
// CROPS the source image — a common-case ~3:4 phone photo in the 176px
// mobile banner shows well under half its height (Codex Phase-5 review). Pin
// coordinates are model-normalized against the FULL source image, so they
// must be projected through the actual cover transform, and pins whose
// center falls in the cropped-away region must hide.

export interface DiagnosisBoundingBox {
  x: number
  y: number
  width: number
  height: number
}

/** Legacy fallback positions retained for import compatibility. New diagnosis
    surfaces never render a pin without a model-supplied valid box: an arbitrary
    marker looks like localized evidence when none exists. */
export const FALLBACK_PIN_POSITIONS: { leftPct: number; topPct: number }[] = [
  { leftPct: 24, topPct: 28 },
  { leftPct: 72, topPct: 38 },
  { leftPct: 46, topPct: 68 },
]

export const MAX_PINS = 3

function isValidBox(box: unknown): box is DiagnosisBoundingBox {
  if (!box || typeof box !== 'object') return false
  const b = box as Record<string, unknown>
  const nums = [b.x, b.y, b.width, b.height]
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return false
  const { x, y, width, height } = b as unknown as DiagnosisBoundingBox
  // Positive area required (mobile parity): a zero-dimension box is a model
  // artifact, not evidence.
  return x >= 0 && y >= 0 && width > 0 && height > 0 && x + width <= 1 && y + height <= 1
}

export interface ProjectedPin {
  /** Percent coordinates within the visible container. */
  leftPct: number
  topPct: number
  /** False when the pin center lies in the cropped-away region (hide it). */
  visible: boolean
  /** True when this is a deterministic fallback, not a real box center. */
  fallback: boolean
}

/**
 * Project a source-normalized box center through the object-cover transform
 * of a `natural`-sized image rendered in a `container`-sized box.
 *
 * cover scale = max(cw/nw, ch/nh); the scaled image is center-aligned, so the
 * overflow on each axis is split evenly and pins can land outside [0, 100]%.
 * Callers pass the MEASURED container and naturalWidth/Height of the <img>;
 * when measurements are unavailable (image not yet loaded), callers should
 * render nothing rather than guess.
 */
export function projectPinThroughCover(
  boundingBox: unknown,
  _index: number,
  natural: { width: number; height: number },
  container: { width: number; height: number },
): ProjectedPin {
  if (!isValidBox(boundingBox)) {
    return { leftPct: 0, topPct: 0, visible: false, fallback: false }
  }
  if (
    !(natural.width > 0 && natural.height > 0 && container.width > 0 && container.height > 0)
  ) {
    return { leftPct: 0, topPct: 0, visible: false, fallback: false }
  }

  const scale = Math.max(container.width / natural.width, container.height / natural.height)
  const displayedWidth = natural.width * scale
  const displayedHeight = natural.height * scale
  const offsetX = (displayedWidth - container.width) / 2
  const offsetY = (displayedHeight - container.height) / 2

  const centerX = (boundingBox.x + boundingBox.width / 2) * displayedWidth - offsetX
  const centerY = (boundingBox.y + boundingBox.height / 2) * displayedHeight - offsetY

  const leftPct = (centerX / container.width) * 100
  const topPct = (centerY / container.height) * 100
  // Small margin so a pin whose center sits a hair outside still shows at the
  // edge instead of popping in/out on resize jitter.
  const visible = leftPct >= -2 && leftPct <= 102 && topPct >= -2 && topPct <= 102

  return {
    leftPct: Math.min(98, Math.max(2, leftPct)),
    topPct: Math.min(98, Math.max(2, topPct)),
    visible,
    fallback: false,
  }
}
